import { useState, useEffect } from 'react';
import { 
  Button,
  TextField,
  MenuItem,
  Stack,
  DialogContent,
  DialogTitle,
  DialogActions,
  Dialog as MuiDialog
} from '@mui/material';
import { 
  MEDIA_RECORD_TYPE_MAP, 
  MediaCommerceDto, 
  CreateMediaCommerceDto,
  UpdateMediaCommerceDto, 
  MediaRecordType
} from '@sr/dto';
import useConfirmDialog from 'hooks/confirm-dialog.hook';
import { Field, Form, Formik, FormikProps } from 'formik';
import { mediaCommerceApi } from './api-slice';
import { useSnack } from 'shared/ui/snack';
import { extractNestJsErrorMessage } from 'shared/api/rtk-query';

type FormData = {
  name: string;
  typeRecord: MediaRecordType;
  durationDays: number | '';
  price: number | '';
  productionPrice: number | '';
  percentages: number | '';
};

const validateForm = (values: FormData) => {
  const errors: Partial<Record<keyof FormData, string>> = {};
  
  if (!values.name.trim()) {
    errors.name = 'Название обязательно';
  }
  
  if (!values.typeRecord) {
    errors.typeRecord = 'Тип записи обязателен';
  }
  
  if (Number(values.durationDays) <= 0) {
    errors.durationDays = 'Продолжительность обязательна и должна быть положительным числом';
  }
  
  if (!values.price || Number(values.price) <= 0) {
    errors.price = 'Цена обязательна и должна быть положительной';
  }
  
  if (values.productionPrice && Number(values.productionPrice) < 0) {
    errors.productionPrice = 'Не может быть отрицательной';
  }
  
  if (values.percentages) {
    const percent = Number(values.percentages);
    if (percent < 0 || percent > 5000) {
      errors.percentages = 'Должно быть от 0 до 5000%';
    }
  }
  
  return errors;
};

const formTools = {
  toFormData: (dto: MediaCommerceDto): FormData => ({
    name: dto.name,
    typeRecord: dto.typeRecord,
    durationDays: dto.durationDays || '',
    price: dto.price || '',
    productionPrice: dto.productionPrice || '',
    percentages: dto.percentages || ''
  }),
  
  toCreateDto: (formData: FormData, mediaId: number): CreateMediaCommerceDto => ({
    name: formData.name,
    mediaId: mediaId,
    typeRecord: formData.typeRecord,
    durationDays: Number(formData.durationDays),
    price: Number(formData.price),
    productionPrice: formData.productionPrice ? Number(formData.productionPrice) : undefined,
    percentages: formData.percentages ? Number(formData.percentages) : undefined
  }),
  
  toUpdateDto: (formData: FormData, id: number): UpdateMediaCommerceDto => ({
    id,
    name: formData.name,
    typeRecord: formData.typeRecord,
    durationDays: Number(formData.durationDays),
    price: Number(formData.price),
    productionPrice: formData.productionPrice ? Number(formData.productionPrice) : undefined,
    percentages: formData.percentages ? Number(formData.percentages) : undefined
  })
};

const Dialog = ({ 
  open, 
  onClose, 
  children,
  maxWidth = 'md',
  fullWidth = true 
}: { 
  open: boolean; 
  onClose: () => void; 
  children: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
}) => {
  return (
    <MuiDialog 
      open={open} 
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      {children}
    </MuiDialog>
  );
};

export const useMediaCommerceDialog = (mediaId: number) => {
  const [open, setOpen] = useState(false);
  const [itemToEdit, setItemToEdit] = useState<MediaCommerceDto | null>(null);
  const [key, setKey] = useState(0);
  const [addMediaCommerce] = mediaCommerceApi.useAddMediaCommerceMutation();
  const { showSuccess, showError } = useSnack();
  const [updateMediaCommerce] = mediaCommerceApi.useUpdateMediaCommerceMutation();
  const openToAdd = () => {
    setItemToEdit(null);
    setKey(prev => prev + 1);
    setOpen(true);
  };
  
  const openToEdit = (item: MediaCommerceDto) => {
    setItemToEdit(item);
    setKey(prev => prev + 1);
    setOpen(true);
  };
  
  const close = () => {
    setOpen(false);
  };
  
  const handleSubmit = async (values: FormData) => {
    try {
      if (itemToEdit) {
        const updateDto = formTools.toUpdateDto(values, itemToEdit.id);
        await updateMediaCommerce(updateDto);
        showSuccess('Строка обновлена в ценах на СМИ');
      } else {
        const createDto = formTools.toCreateDto(values, mediaId);
        await addMediaCommerce(createDto);
        showSuccess('Строка добавлена в цены на СМИ');
      }
      close();
      setItemToEdit(null);
    } catch (error) {
      showError(`Ошибка:${extractNestJsErrorMessage(error)}`);
    }
  };
  
  const initialValues: FormData = itemToEdit 
    ? formTools.toFormData(itemToEdit)
    : {
      name: '',
      typeRecord: MediaRecordType.INACTIVE,
      durationDays: '',
      price: '',
      productionPrice: '',
      percentages: ''
    };
  
  return {
    dialogProps: {
      mediaId,
      open,
      onClose: close,
      itemToEdit,
      initialValues,
      onSubmit: handleSubmit,
      isSubmitting: false,
      key
    },
    openToAdd,
    openToEdit,
    close
  };
};

export const MediaCommerceFormDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  itemToEdit: MediaCommerceDto | null;
  initialValues: FormData;
  onSubmit: (values: FormData) => Promise<void>;
  isSubmitting: boolean;
  mediaId: number;
  key: number;
}> = ({ open, onClose, itemToEdit, initialValues, onSubmit, isSubmitting, mediaId, key }) => {
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const confirmDialog = useConfirmDialog();
  
  const handleClose = (dirty: boolean, resetForm: () => void) => {
    if (dirty) {
      confirmDialog({ content: 'Несохраненные данные будут утеряны' }).then(() => {
        resetForm();
        onClose();
      });
    } else {
      setHasSubmitted(false);
      onClose();
    }
  };

  useEffect(() => {
    if (!open) {
      setHasSubmitted(false);
    }
  }, [open]);

  return (
    <>
      <Formik
        key={key}
        initialValues={initialValues}
        validate={validateForm}
        validateOnChange={hasSubmitted}
        validateOnBlur={hasSubmitted}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {(formikProps: FormikProps<FormData>) => {
          const { dirty, resetForm, isValid, errors, touched } = formikProps;
          
          return (
            <Dialog
              open={open}
              onClose={() => {
                if (!isSubmitting) {
                  handleClose(dirty, resetForm);
                }
              }}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>
                {itemToEdit ? 'Редактировать запись' : 'Новая запись'}
                <Button
                  sx={{ position: 'absolute', right: 8, top: 8 }}
                  onClick={() => handleClose(dirty, resetForm)}
                  disabled={isSubmitting}
                  size="small"
                >
                  ✕
                </Button>
              </DialogTitle>
              
              <Form>
                <DialogContent dividers>
                  <Stack spacing={3} sx={{ mt: 1 }}>
                    <Field
                      required
                      name="name"
                      as={TextField}
                      label="Название"
                      fullWidth
                      disabled={isSubmitting}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                    />

                    <Field
                      required
                      name="typeRecord"
                      as={TextField}
                      select
                      label="Тип записи"
                      fullWidth
                      disabled={isSubmitting}
                      error={touched.typeRecord && Boolean(errors.typeRecord)}
                      helperText={touched.typeRecord && errors.typeRecord}
                    >
                      {Object.entries(MEDIA_RECORD_TYPE_MAP).map(([key, value]) => (
                        <MenuItem key={key} value={key}> 
                          {value}
                        </MenuItem>
                      ))}
                    </Field>

                    <Stack direction="row" spacing={2}>
                      <Field
                        name="durationDays"
                        as={TextField}
                        label="Продолжительность (дней)"
                        fullWidth
                        disabled={isSubmitting}
                        type="number"
                        error={touched.durationDays && Boolean(errors.durationDays)}
                        helperText={touched.durationDays && errors.durationDays}
                      />
                      
                      <Field
                        name="price"
                        as={TextField}
                        label="Цена"
                        fullWidth
                        disabled={isSubmitting}
                        type="number"
                        InputProps={{
                          inputProps: { min: 0, step: 0.01 }
                        }}
                        error={touched.price && Boolean(errors.price)}
                        helperText={touched.price && errors.price}
                      />
                    </Stack>
                    
                    <Stack direction="row" spacing={2}>
                      <Field
                        name="productionPrice"
                        as={TextField}
                        label="Цена производства"
                        fullWidth
                        disabled={isSubmitting}
                        type="number"
                        InputProps={{
                          inputProps: { min: 0, step: 0.01 }
                        }}
                        error={touched.productionPrice && Boolean(errors.productionPrice)}
                        helperText={touched.productionPrice && errors.productionPrice}
                      />
                      
                      <Field
                        name="percentages"
                        as={TextField}
                        label="Целые проценты (%)"
                        fullWidth
                        disabled={isSubmitting}
                        type="number"
                        InputProps={{
                          inputProps: { min: 0, max: 5000, step: 1 }
                        }}
                        error={touched.percentages && Boolean(errors.percentages)}
                        helperText={touched.percentages && errors.percentages}
                      />
                    </Stack>
                  </Stack>
                </DialogContent>
                
                <DialogActions>
                  <Button
                    variant="outlined"
                    onClick={() => handleClose(dirty, resetForm)}
                    disabled={isSubmitting}
                    sx={{ mr: 1 }}
                  >
                    Отмена
                  </Button>
                  
                  <Button
                    variant="contained"
                    type="submit"
                    onClick={() => setHasSubmitted(true)}
                    disabled={isSubmitting || !dirty || !isValid}
                  >
                    {isSubmitting ? 'Сохранение...' : 'Сохранить'}
                  </Button>
                </DialogActions>
              </Form>
            </Dialog>
          );
        }}
      </Formik>
    </>
  );
};